import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { businessReportsApi, type PnlAestheticProceduresBreakdownResponse } from '@/api/businessReports';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const REPORT_IST = 'Asia/Kolkata';

function formatIst(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: REPORT_IST,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(d);
}

type Props = {
  periodMode: 'MTD' | 'YTD';
  periodKey: string;
  fyStartYear?: number;
  hospitalId?: string;
  className?: string;
  children?: ReactNode;
};

function BreakdownPanel({
  data,
  loading,
}: {
  data?: PnlAestheticProceduresBreakdownResponse;
  loading: boolean;
}) {
  if (loading) {
    return <p className="text-xs text-muted-foreground">Loading patient details…</p>;
  }
  if (!data) {
    return <p className="text-xs text-muted-foreground">No data.</p>;
  }

  const m = data.metrics;
  return (
    <div className="space-y-2 max-w-lg">
      <p className="text-[11px] text-muted-foreground leading-snug">
        Distinct patients with Additional Services bills ≥ ₹
        {m.minBillAmount.toLocaleString('en-IN')} where the attributed doctor is on the{' '}
        {m.teamLeadName && m.teamDoctorCount > 0 ? (
          <>
            <strong>{m.teamLeadName}</strong> plastic surgery team ({m.teamDoctorCount} doctors){' '}
            or{' '}
          </>
        ) : null}
        in the <strong>Dermatology</strong> department. Bill doctor comes from the Additional
        Services bill (recommended doctor) or linked OPD appointment. Period: {data.periodLabel}.
      </p>
      <p className="text-[11px] font-medium text-slate-700">
        Count: {m.patientCount} patient{m.patientCount === 1 ? '' : 's'}
      </p>
      {data.items.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">No qualifying bills in this period.</p>
      ) : (
        <div className="max-h-[320px] overflow-auto rounded border">
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="text-left p-1.5 font-medium">Patient</th>
                <th className="text-left p-1.5 font-medium">Service</th>
                <th className="text-left p-1.5 font-medium">Doctor</th>
                <th className="text-right p-1.5 font-medium">Line</th>
                <th className="text-right p-1.5 font-medium">Bill</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((row, i) => (
                <tr key={`${row.billId}-${row.serviceName}-${i}`} className="border-t">
                  <td className="p-1.5 align-top">
                    <div className="font-medium">{row.patientName}</div>
                    <div className="text-muted-foreground">{row.uhid ?? '—'}</div>
                    <div className="text-muted-foreground whitespace-nowrap">{formatIst(row.billDate)}</div>
                  </td>
                  <td className="p-1.5 align-top">{row.serviceName}</td>
                  <td className="p-1.5 align-top">{row.doctorName ?? '—'}</td>
                  <td className="p-1.5 text-right tabular-nums align-top">
                    ₹{row.lineAmount.toLocaleString('en-IN')}
                  </td>
                  <td className="p-1.5 text-right tabular-nums align-top">
                    ₹{row.billTotal.toLocaleString('en-IN')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data.truncated ? (
        <p className="text-[11px] text-amber-700">List truncated — first 500 service lines shown.</p>
      ) : null}
    </div>
  );
}

export function PnlAestheticProceduresHover({
  periodMode,
  periodKey,
  fyStartYear,
  hospitalId,
  className,
  children,
}: Props) {
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: [
      'pnl-aesthetic-procedures-breakdown',
      periodMode,
      periodKey,
      fyStartYear,
      hospitalId,
    ],
    queryFn: () =>
      businessReportsApi.pnlAestheticProceduresBreakdown({
        periodMode,
        month: periodMode === 'MTD' ? periodKey : undefined,
        fy: periodMode === 'YTD' ? fyStartYear : undefined,
        hospitalId,
      }),
    enabled: open,
    staleTime: 60_000,
  });

  const trigger = children ?? (
    <span className="inline-flex items-center gap-1 cursor-help underline decoration-dotted underline-offset-2">
      No of Aesthetic Procedures
      <Info className="h-3.5 w-3.5 text-muted-foreground" />
    </span>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <span
          className={cn('inline-block', className)}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        >
          {trigger}
        </span>
      </PopoverAnchor>
      <PopoverContent
        className="w-auto p-3"
        side="right"
        align="start"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <BreakdownPanel data={data} loading={isLoading} />
      </PopoverContent>
    </Popover>
  );
}
